/**
 * Schema.org 结构化数据生成工具
 * @see https://schema.org/
 */

import type {
  IWebSite,
  IOrganization,
  IProduct,
  IFAQPage,
  IReview,
  IAggregateRating,
  IOffer,
  IBrand,
  IQuestion,
  SchemaType,
} from '@/types/schema'

/**
 * Schema.org 上下文 URL
 */
const SCHEMA_CONTEXT = 'https://schema.org'

/**
 * 生成完整的 JSON-LD 格式数据
 */
export function toJsonLd(schema: SchemaType): string {
  const data = {
    '@context': SCHEMA_CONTEXT,
    ...schema,
  }
  return JSON.stringify(data, null, 2)
}

/**
 * 生成 WebSite Schema
 */
export function createWebSiteSchema(options: Partial<IWebSite> = {}): IWebSite {
  return {
    '@type': 'WebSite',
    '@context': SCHEMA_CONTEXT,
    name: 'Flashbase',
    url: window.location.origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${window.location.origin}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
    ...options,
  }
}

/**
 * 生成 Organization Schema
 */
export function createOrganizationSchema(options: Partial<IOrganization> = {}): IOrganization {
  return {
    '@type': 'Organization',
    '@context': SCHEMA_CONTEXT,
    name: 'Flashbase',
    legalName: 'Flashbase Inc.',
    url: window.location.origin,
    logo: `${window.location.origin}/images/console_logo.svg`,
    sameAs: [
      'https://www.facebook.com/flashbase',
      'https://twitter.com/flashbase',
      'https://www.linkedin.com/company/flashbase',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-xxx-xxxx',
      contactType: 'customer service',
      availableLanguage: ['zh-CN', 'en-US'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '',
      addressLocality: '',
      addressRegion: '',
      postalCode: '',
      addressCountry: 'CN',
    },
    ...options,
  }
}

/**
 * 生成 Product Schema
 */
export function createProductSchema(options: Partial<IProduct> = {}): IProduct {
  return {
    '@type': 'Product',
    '@context': SCHEMA_CONTEXT,
    brand: {
      '@type': 'Brand',
      name: 'Flashbase',
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'CNY',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
    ...options,
  }
}

/**
 * 生成 FAQPage Schema
 */
export function createFAQPageSchema(
  questions: IQuestion[] = [],
  options: Partial<IFAQPage> = {}
): IFAQPage {
  return {
    '@type': 'FAQPage',
    '@context': SCHEMA_CONTEXT,
    mainEntity: questions,
    ...options,
  }
}

/**
 * 生成 Review Schema
 */
export function createReviewSchema(options: Partial<IReview> = {}): IReview {
  return {
    '@type': 'Review',
    ...options,
  }
}

/**
 * 生成 AggregateRating Schema
 */
export function createAggregateRatingSchema(
  options: Partial<IAggregateRating> = {}
): IAggregateRating {
  return {
    '@type': 'AggregateRating',
    ratingValue: 5,
    reviewCount: 1,
    bestRating: 5,
    worstRating: 1,
    ...options,
  }
}

/**
 * 生成 Offer Schema
 */
export function createOfferSchema(options: Partial<IOffer> = {}): IOffer {
  return {
    '@type': 'Offer',
    priceCurrency: 'CNY',
    availability: 'https://schema.org/InStock',
    itemCondition: 'https://schema.org/NewCondition',
    ...options,
  }
}

/**
 * 生成 Brand Schema
 */
export function createBrandSchema(options: Partial<IBrand> = {}): IBrand {
  return {
    '@type': 'Brand',
    name: 'Flashbase',
    ...options,
  }
}

/**
 * 将多个 Schema 组合为一个数组
 * 用于在页面中同时注入多个 Schema
 */
export function createMultipleSchemas(schemas: SchemaType[]): string {
  const data = schemas.map((schema) => ({
    '@context': SCHEMA_CONTEXT,
    ...schema,
  }))
  return JSON.stringify(data, null, 2)
}

/**
 * 创建 script 标签用于嵌入 HTML
 */
export function createSchemaScriptTag(jsonLd: string): HTMLScriptElement {
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.textContent = jsonLd
  return script
}

/**
 * 从 HTML 头部移除所有结构化数据 script 标签
 */
export function removeSchemaScripts(): void {
  const existingScripts = document.querySelectorAll(
    'script[type="application/ld+json"]'
  )
  existingScripts.forEach((script) => script.remove())
}

/**
 * 向 HTML 头部添加结构化数据
 */
export function injectSchema(schema: SchemaType | SchemaType[]): void {
  removeSchemaScripts()
  
  let jsonLd: string
  if (Array.isArray(schema)) {
    jsonLd = createMultipleSchemas(schema)
  } else {
    jsonLd = toJsonLd(schema)
  }
  
  const script = createSchemaScriptTag(jsonLd)
  document.head.appendChild(script)
}
